import React from 'react';
import { Box, Typography, Button, Divider } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { COLORS } from '../styles';
import { getFriendlyName, getDrvLabel } from '../utils';

function ProfileScreen({ driver }) {
  const name = getFriendlyName(driver?.id);
  const drv = getDrvLabel(driver?.name);

  // Demo stats
  const stats = {
    today: { bags: 142, stops: 18, runs: 3 },
    week:  { bags: 894, stops: 121, runs: 22 },
    all:   { bags: 38420, stops: 5103, runs: 962 },
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      {/* Hero card */}
      <Box sx={{
        background: `linear-gradient(160deg, ${COLORS.accentDim} 0%, ${COLORS.deviceChassis} 100%)`,
        p: 2.5,
        textAlign: 'center',
      }}>
        <Box sx={{
          width: 70, height: 70, borderRadius: '50%',
          backgroundColor: COLORS.surface,
          border: `3px solid ${COLORS.accent}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          mb: 1,
        }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: COLORS.accent }}>
            {name.charAt(0).toUpperCase()}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '1.2rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
          {name}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary, mt: 0.25 }}>
          {drv} · AM Shift
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
          <BadgeIcon sx={{ fontSize: 12, color: COLORS.textMuted }} />
          <Typography sx={{ fontSize: '0.65rem', color: COLORS.textMuted }}>
            Badge #84219
          </Typography>
        </Box>
      </Box>

      {/* Stats sections */}
      <Box sx={{ p: 2 }}>
        <StatBlock title="Today" data={stats.today} />
        <StatBlock title="This Week" data={stats.week} />
        <StatBlock title="All Time" data={stats.all} />
      </Box>

      <Divider sx={{ borderColor: COLORS.divider, mx: 2 }} />

      {/* Settings list */}
      <Box sx={{ p: 1 }}>
        <RowAction icon={<SettingsIcon sx={{ fontSize: 18 }} />} label="Settings" />
        <RowAction icon={<HelpOutlineIcon sx={{ fontSize: 18 }} />} label="Help & Support" />
      </Box>

      <Box sx={{ p: 2, mt: 'auto' }}>
        <Button
          fullWidth
          startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
          sx={{
            color: COLORS.danger,
            borderColor: COLORS.danger,
            border: `1px solid ${COLORS.danger}`,
            fontWeight: 700,
            fontSize: '0.78rem',
            py: 1,
            '&:hover': { backgroundColor: 'rgba(244, 63, 94, 0.08)' },
          }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );
}

function StatBlock({ title, data }) {
  return (
    <Box sx={{ mb: 1.75 }}>
      <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <StatTile label="Bags" value={data.bags.toLocaleString()} />
        <StatTile label="Stops" value={data.stops.toLocaleString()} />
        <StatTile label="Runs" value={data.runs.toLocaleString()} />
      </Box>
    </Box>
  );
}

function StatTile({ label, value }) {
  return (
    <Box sx={{
      flex: 1,
      backgroundColor: COLORS.surface,
      borderRadius: 1.5,
      p: 1,
      textAlign: 'center',
    }}>
      <Typography sx={{ color: COLORS.accent, fontSize: '0.95rem', fontWeight: 700, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ color: COLORS.textMuted, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25 }}>
        {label}
      </Typography>
    </Box>
  );
}

function RowAction({ icon, label }) {
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      p: 1.25,
      borderRadius: 1,
      cursor: 'pointer',
      color: COLORS.textPrimary,
      '&:hover': { backgroundColor: COLORS.surface },
    }}>
      {icon}
      <Typography sx={{ fontSize: '0.8rem', flex: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.85rem', color: COLORS.textMuted }}>›</Typography>
    </Box>
  );
}

export default ProfileScreen;
